import { LocalStorageApi } from 'helpers/local-storage';
import { calculateMd5File } from 'helpers/calculate-file-md5';
import {
  nextUploadId,
  publishUploadProgress,
} from 'helpers/upload-progress-bus';
import { UploadFileController } from 'networking/controllers/upload-file.controller';

export type DirectUploadResult = { id: number; signedId: string };

// Promise-based S3 upload (presigned URL + XHR PUT). Unlike the stateful
// useFileUpload hook this owns no component state, so callers can close a
// modal and let the upload keep running in the background.
export const directUploadFile = async (
  file: File,
): Promise<DirectUploadResult> => {
  const md5 = await calculateMd5File(file);
  const presigned = await UploadFileController.presignedUrl(file, md5);

  const uploadId = nextUploadId();
  await new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', presigned.directUpload.url);
    request.setRequestHeader('X-CSRF-Token', LocalStorageApi.get('csrfToken'));
    Object.entries(presigned.directUpload.headers).forEach(([key, value]) => {
      request.setRequestHeader(key, value as string);
    });
    // Feed the live storage indicator (top bar) with real byte progress.
    request.upload.addEventListener('progress', (e) => {
      publishUploadProgress({
        uploadId,
        totalBytes: file.size,
        loadedBytes: e.loaded,
      });
    });
    const finish = (ok: boolean) =>
      publishUploadProgress({
        uploadId,
        totalBytes: file.size,
        loadedBytes: ok ? file.size : 0,
        done: true,
      });
    request.addEventListener('load', () => {
      finish(request.status >= 200 && request.status < 300);
      if (request.status >= 200 && request.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${request.status}`));
      }
    });
    request.addEventListener('error', () => {
      finish(false);
      reject(new Error('Upload failed'));
    });
    request.addEventListener('abort', () => {
      finish(false);
      reject(new Error('Upload aborted'));
    });
    request.send(file);
  });

  return { id: presigned.id, signedId: presigned.signedId };
};
